import {naturalSortByKey} from '@benchling/js-utils';
import {applyEdgeChanges, applyNodeChanges, type Edge, EdgeChange, type Node, NodeChange} from 'reactflow';
import {ActorRefFrom, assertEvent, assign, enqueueActions, fromPromise, setup} from 'xstate';

import {LayoutSettings} from 'ui-library/reactflow/layout/calculateDimensionsForLayout';
import layoutFlowchart from 'ui-library/reactflow/layout/layoutFlowchart';

import {
  EntityMap_entityMap_EntityMapNode_connectedSchemas_EntityMapConnectedSchemaNode as EntityMapConnectedSchemaNode,
  EntityMap_entityMap_EntityMapNode_entities as EntityMapEntityNodeOrInaccessibleInfoNode,
} from './__generated/EntityMap';
import {
  DEFAULT_LAYOUT_DEBOUNCE_MS,
  ENTITY_MAP_TOTAL_NODE_LIMIT,
  INITIAL_DEPTH_ON_LOAD,
} from './EntityMapConstants';
import {fetchEntityMap} from './EntityMapDAO';
import {
  EntityMapGraph,
  extractFullyExpandedEntityIds,
  mergeGraphNodesById,
  parseEntityMapIntoEntityMapGraph,
} from './EntityMapUtils';
import {EntityNodeMachine} from './EntityNodeMachine';

/**
 * Merges two maps, preserving (by key) the values from the first map.
 */
const mergeMapsPreservingExisting = <K, V>(originalMap: Map<K, V>, newMap: Map<K, V>): Map<K, V> =>
  new Map([...newMap.entries(), ...originalMap.entries()]);

function deduplicateConnectedSchemas(
  existing: Array<EntityMapConnectedSchemaNode>,
  incoming: Array<EntityMapConnectedSchemaNode>
): Array<EntityMapConnectedSchemaNode> {
  const existingIds = new Set(existing.map((s) => s.id));
  return [...existing, ...incoming.filter((s) => !existingIds.has(s.id))];
}

interface LayoutGraphInput {
  graphNodesById: Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>;
  graphEdgesById: Map<string, Edge>;
  layoutSettings: Partial<LayoutSettings<Node<EntityMapEntityNodeOrInaccessibleInfoNode>, Edge>>;
}

interface LayoutGraphOutput {
  graphNodesById: Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>;
  graphEdgesById: Map<string, Edge>;
}

interface EntityMapInput {
  entityId: string;
  layoutSettings: Partial<LayoutSettings<Node<EntityMapEntityNodeOrInaccessibleInfoNode>, Edge>>;
  excludedSchemaIds: Array<string>;
}

interface EntityMapContext extends EntityMapGraph {
  entityId: string;
  layoutSettings: Partial<LayoutSettings<Node<EntityMapEntityNodeOrInaccessibleInfoNode>, Edge>>;
  excludedSchemaIds: Array<string>;
  spawnedNodeMachines: Map<string, ActorRefFrom<typeof EntityNodeMachine>>;
  fullyExpandedEntityIds: Set<string>;
  isEntityMapSizeOverProductLimit: boolean;
}

interface CompletedFetchingExtendedGraphEvent extends EntityMapGraph {
  type: 'completedFetchingExtendedGraph';
}

type EntityMapEvents =
  | {
      type: 'onNodesChange';
      changes: Array<NodeChange>;
    }
  | {
      type: 'onEdgesChange';
      changes: Array<EdgeChange>;
    }
  | {
      type: 'updateExcludedSchemas';
      excludedSchemaIds: Array<string>;
    }
  | CompletedFetchingExtendedGraphEvent;

/**
 * XState state machine for managing the entity map graph for a given entityId.
 * Handles idle, initial loading, and error states.
 * Spawns child entity node machines for each entity in the graph, and stores them in context keyed by its
 * entityId. Listens for completedFetchingExtendedGraph events, which are sent from the child entity node
 * machines when they have loaded their relationships.
 * Stores graphNodesById and graphEdgesById in context.
 *
 * The idle state handles onNodesChange and onEdgesChange events, which can be sent from the view using
 * actorRef.send().
 */
export const EntityMapMachine = setup({
  types: {
    input: {} as EntityMapInput,
    context: {} as EntityMapContext,
    events: {} as EntityMapEvents,
  },
  actors: {
    loadInitialGraph: fromPromise<EntityMapGraph, {entityId: string; excludedSchemaIds: Array<string>}>(
      async ({input: {entityId, excludedSchemaIds}}) => {
        const entityMap = await fetchEntityMap(entityId, INITIAL_DEPTH_ON_LOAD, excludedSchemaIds);
        return parseEntityMapIntoEntityMapGraph(entityMap);
      }
    ),
    layoutGraph: fromPromise<LayoutGraphOutput, LayoutGraphInput>(
      async ({input: {graphNodesById, graphEdgesById, layoutSettings}}) => {
        const nodes = naturalSortByKey(Array.from(graphNodesById.values()), 'id');
        const edges = naturalSortByKey(Array.from(graphEdgesById.values()), 'id');

        const {nodes: layoutNodes, edges: layoutEdges} = await layoutFlowchart(
          {nodes, edges},
          layoutSettings
        );

        return {
          graphNodesById: new Map(layoutNodes.map((node) => [node.id, node])),
          graphEdgesById: new Map(layoutEdges.map((edge) => [edge.id, edge])),
        };
      }
    ),
  },
  actions: {
    mergeGraph: assign(({spawn, context}, params: EntityMapGraph) => {
      // Preserve existing nodes' calculated dimensions so they don't jump around or overlap.
      const graphNodesById = mergeGraphNodesById(context.graphNodesById, params.graphNodesById);
      const graphEdgesById = mergeMapsPreservingExisting(context.graphEdgesById, params.graphEdgesById);
      const connectedSchemas = deduplicateConnectedSchemas(context.connectedSchemas, params.connectedSchemas);
      const fullyExpandedEntityIds = extractFullyExpandedEntityIds({graphNodesById, graphEdgesById});

      const spawnedNodeMachines = new Map(context.spawnedNodeMachines);
      const newEntityIds = Array.from(params.graphNodesById.keys()).filter(
        (entityId) => !spawnedNodeMachines.has(entityId)
      );
      for (const entityId of newEntityIds) {
        const entity = graphNodesById.get(entityId)!.data;
        const entityNodeMachine = spawn(EntityNodeMachine, {
          input: {
            entityId,
            entity,
            mainEntityId: context.entityId,
            excludedSchemaIds: context.excludedSchemaIds,
          },
        });
        spawnedNodeMachines.set(entityId, entityNodeMachine);
      }

      return {
        graphNodesById,
        graphEdgesById,
        connectedSchemas,
        fullyExpandedEntityIds,
        spawnedNodeMachines,
        isEntityMapSizeOverProductLimit: graphNodesById.size >= ENTITY_MAP_TOTAL_NODE_LIMIT,
      };
    }),
    onNodesChange: assign(({context, event}) => {
      assertEvent(event, 'onNodesChange');
      const nodes: Array<Node> = [...context.graphNodesById.values()];
      const updatedNodes = applyNodeChanges(event.changes, nodes);
      return {
        graphNodesById: new Map(updatedNodes.map((node) => [node.id, node])),
      };
    }),
    onEdgesChange: assign(({context, event}) => {
      assertEvent(event, 'onEdgesChange');
      const edges: Array<Edge> = [...context.graphEdgesById.values()];
      const updatedEdges = applyEdgeChanges(event.changes, edges);
      return {
        graphEdgesById: new Map(updatedEdges.map((edge) => [edge.id, edge])),
      };
    }),
    updateExcludedSchemaIds: assign(({event}) => {
      assertEvent(event, 'updateExcludedSchemas');
      return {
        excludedSchemaIds: event.excludedSchemaIds,
      };
    }),
    replaceGraph: enqueueActions(({enqueue, context}, params: EntityMapGraph) => {
      for (const actor of context.spawnedNodeMachines.values()) {
        enqueue.stopChild(actor);
      }

      enqueue.assign(({spawn: spawnFn}) => {
        const graphNodesById = new Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>();
        for (const [id, node] of params.graphNodesById) {
          const existing = context.graphNodesById.get(id);
          graphNodesById.set(id, existing ? {...node, position: existing.position} : node);
        }
        const graphEdgesById = params.graphEdgesById;
        const connectedSchemas = deduplicateConnectedSchemas(
          context.connectedSchemas,
          params.connectedSchemas
        );
        const fullyExpandedEntityIds = extractFullyExpandedEntityIds({graphNodesById, graphEdgesById});

        const spawnedNodeMachines = new Map<string, ActorRefFrom<typeof EntityNodeMachine>>();
        for (const entityId of graphNodesById.keys()) {
          const entity = graphNodesById.get(entityId)!.data;
          const entityNodeMachine = spawnFn(EntityNodeMachine, {
            input: {
              entityId,
              entity,
              mainEntityId: context.entityId,
              excludedSchemaIds: context.excludedSchemaIds,
            },
          });
          spawnedNodeMachines.set(entityId, entityNodeMachine);
        }

        return {
          graphNodesById,
          graphEdgesById,
          connectedSchemas,
          fullyExpandedEntityIds,
          spawnedNodeMachines,
          isEntityMapSizeOverProductLimit: graphNodesById.size >= ENTITY_MAP_TOTAL_NODE_LIMIT,
        };
      });
    }),
  },
}).createMachine({
  id: 'entityMapMachine',
  context: ({input}) => ({
    entityId: input.entityId,
    layoutSettings: input.layoutSettings,
    excludedSchemaIds: input.excludedSchemaIds,
    graphNodesById: new Map(),
    graphEdgesById: new Map(),
    connectedSchemas: [],
    spawnedNodeMachines: new Map(),
    fullyExpandedEntityIds: new Set(),
    isEntityMapSizeOverProductLimit: false,
  }),
  initial: 'loading',
  states: {
    loading: {
      invoke: {
        id: 'loadInitialGraphActorFromLoadingState',
        input: ({context}) => ({entityId: context.entityId, excludedSchemaIds: context.excludedSchemaIds}),
        src: 'loadInitialGraph',
        onDone: {
          target: 'layouting',
          actions: [
            {
              type: 'mergeGraph',
              params: ({event}) => event.output,
            },
          ],
        },
        onError: {
          target: 'error',
        },
      },
    },
    layouting: {
      invoke: {
        id: 'layoutGraphActor',
        input: ({context}) => ({
          graphNodesById: context.graphNodesById,
          graphEdgesById: context.graphEdgesById,
          layoutSettings: context.layoutSettings,
        }),
        src: 'layoutGraph',
        onDone: {
          target: 'idle',
          actions: [assign(({event}) => event.output)],
        },
        onError: {
          target: 'error',
        },
      },
      on: {
        onNodesChange: {
          actions: ['onNodesChange'],
          target: 'debouncingLayout',
        },
        onEdgesChange: {
          actions: ['onEdgesChange'],
          target: 'debouncingLayout',
        },
        completedFetchingExtendedGraph: {
          target: 'debouncingLayout',
          actions: [
            {
              type: 'mergeGraph',
              params: ({event: {type, ...entityMapGraph}}) => entityMapGraph,
            },
          ],
        },
        updateExcludedSchemas: {
          actions: ['updateExcludedSchemaIds'],
          target: 'refetching',
        },
      },
    },
    idle: {
      on: {
        onNodesChange: {
          actions: ['onNodesChange'],
          target: 'debouncingLayout',
        },
        onEdgesChange: {
          actions: ['onEdgesChange'],
          target: 'debouncingLayout',
        },
        completedFetchingExtendedGraph: {
          target: 'debouncingLayout',
          actions: [
            {
              type: 'mergeGraph',
              params: ({event: {type, ...entityMapGraph}}) => entityMapGraph,
            },
          ],
        },
        updateExcludedSchemas: {
          actions: ['updateExcludedSchemaIds'],
          target: 'refetching',
        },
      },
    },
    debouncingLayout: {
      on: {
        onNodesChange: {
          actions: ['onNodesChange'],
          target: 'debouncingLayout',
          reenter: true,
        },
        onEdgesChange: {
          actions: ['onEdgesChange'],
          target: 'debouncingLayout',
          reenter: true,
        },
        completedFetchingExtendedGraph: {
          actions: [
            {
              type: 'mergeGraph',
              params: ({event: {type, ...entityMapGraph}}) => entityMapGraph,
            },
          ],
          target: 'debouncingLayout',
          reenter: true,
        },
        updateExcludedSchemas: {
          actions: ['updateExcludedSchemaIds'],
          target: 'refetching',
        },
      },
      after: {
        [DEFAULT_LAYOUT_DEBOUNCE_MS]: {
          target: 'layouting',
        },
      },
    },
    refetching: {
      invoke: {
        id: 'refetchGraphActor',
        input: ({context}) => ({entityId: context.entityId, excludedSchemaIds: context.excludedSchemaIds}),
        src: 'loadInitialGraph',
        onDone: {
          target: 'debouncingLayout',
          actions: [
            {
              type: 'replaceGraph',
              params: ({event}) => event.output,
            },
          ],
        },
        onError: {
          target: 'idle',
        },
      },
      on: {
        updateExcludedSchemas: {
          actions: ['updateExcludedSchemaIds'],
          target: 'refetching',
          reenter: true,
        },
      },
    },
    error: {},
  },
});
